import { useState } from "react";
import { motion } from "framer-motion";
import {
  Phone,
  Mic,
  Compass,
  HelpCircle,
  MessageSquare,
  MapPin,
  Mail,
  Youtube,
  Globe,
  Send,
  ArrowUpRight,
} from "lucide-react";
import BookingModal from "@/components/landing/BookingModal";
import PodcastGuestModal from "@/components/landing/PodcastGuestModal";
import DiscoveryCallModal from "@/components/landing/DiscoveryCallModal";
import LocationModal from "@/components/landing/LocationModal";
import ContactFormModal from "@/components/landing/ContactFormModal";
import PricingFAQModal from "@/components/landing/PricingFAQModal";
import PackageQuiz from "@/components/landing/PackageQuiz";
import { FloatingOrbs, AmbientDust, PulseDot } from "@/components/landing/AmbientMotion";

const LOGO = "/brand/sb-logo.svg";

/**
 * /links — hidden "linktree" style page for social bios.
 * Vertical stack of CTA pills that trigger the same modals used across the
 * marketing site. No nav / no footer / not linked from anywhere — direct URL
 * (sbmediastudio.com/links) only.
 */
const LinksPage = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);

  // Each pill: { key, label, sub, icon, color, onClick, external }
  const pills = [
    {
      key: "book",
      label: "Book a studio session",
      sub: "Reserve the room · pick your date",
      icon: Mic,
      color: "#00f0ff",
      onClick: () => setBookingOpen(true),
    },
    {
      key: "discovery",
      label: "Book a free discovery call",
      sub: "30 min · on us · no commitment",
      icon: Phone,
      color: "#22c55e",
      onClick: () => setDiscoveryOpen(true),
    },
    {
      key: "guest",
      label: "Be a Spotlight guest",
      sub: "Free podcast feature · giving back to our community",
      icon: Send,
      color: "#ff0055",
      onClick: () => setGuestOpen(true),
    },
    {
      key: "quiz",
      label: "Take the package quiz",
      sub: "5 questions · find your fit",
      icon: Compass,
      color: "#a78bfa",
      onClick: () => setQuizOpen(true),
    },
    {
      key: "faq",
      label: "Pricing FAQ",
      sub: "10 answers to what everyone asks",
      icon: HelpCircle,
      color: "#00f0ff",
      onClick: () => setFaqOpen(true),
    },
    {
      key: "contact",
      label: "Contact us",
      sub: "Drop a note · reply within 24 hrs",
      icon: MessageSquare,
      color: "#ff0055",
      onClick: () => setContactOpen(true),
    },
    {
      key: "location",
      label: "Studio location",
      sub: "Lakeland, FL · get directions",
      icon: MapPin,
      color: "#a78bfa",
      onClick: () => setLocationOpen(true),
    },
    {
      key: "email",
      label: "Email us directly",
      sub: "info@sbmediastudio.com",
      icon: Mail,
      color: "#22c55e",
      href: "mailto:info@sbmediastudio.com",
    },
    {
      key: "youtube",
      label: "Small Business Spotlight on YouTube",
      sub: "Watch the latest episodes",
      icon: Youtube,
      color: "#ff0055",
      href: "https://www.youtube.com/@sb_spotlight",
      external: true,
    },
    {
      key: "site",
      label: "Visit the full website",
      sub: "sbmediastudio.com",
      icon: Globe,
      color: "#00f0ff",
      href: "/",
    },
  ];

  return (
    <div
      className="relative min-h-screen bg-[#05050a] text-white overflow-hidden"
      data-testid="links-page"
    >
      {/* Ambient scenery */}
      <FloatingOrbs colors={["#00f0ff", "#ff0055", "#a78bfa"]} />
      <AmbientDust count={22} color="#00f0ff" />

      {/* Aurora bg */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-[8%] left-[50%] -translate-x-1/2 w-[720px] h-[720px] rounded-full bg-[#6b21d9]/25 blur-[160px]" />
        <div className="absolute bottom-[-20%] left-[10%] w-[420px] h-[420px] rounded-full bg-[#ff0055]/12 blur-[140px]" />
        <div className="absolute top-[35%] right-[8%] w-[420px] h-[420px] rounded-full bg-[#00f0ff]/12 blur-[140px]" />
      </div>
      <div className="absolute inset-0 -z-10 scanline opacity-25 pointer-events-none" />

      <div className="relative max-w-[520px] mx-auto px-5 md:px-6 pt-14 md:pt-20 pb-16">
        {/* Header: logo + brand + tagline */}
        <motion.div
          initial={{ opacity: 0, y: -18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col items-center text-center mb-10 md:mb-12"
        >
          <div
            className="relative w-24 h-24 md:w-28 md:h-28 rounded-3xl border border-white/15 mb-5 flex items-center justify-center bg-gradient-to-br from-[#00f0ff]/15 via-[#6b21d9]/25 to-[#ff0055]/15"
            style={{ boxShadow: "0 0 40px -8px rgba(0,240,255,0.35)" }}
          >
            <img
              src={LOGO}
              alt="SB Media Studio"
              className="w-14 h-14 md:w-16 md:h-16 object-contain"
              style={{ filter: "invert(1) brightness(1.15)" }}
            />
          </div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#22c55e] mb-3">
            <PulseDot color="#22c55e" size={7} />
            Now booking · Lakeland, FL
          </div>
          <h1
            className="font-display font-black text-3xl md:text-4xl tracking-tighter leading-[1.05]"
            data-testid="links-heading"
          >
            SB Media <span className="text-[#00f0ff] text-glow-cyan">Studio</span>
          </h1>
          <p className="text-[#a395c3] text-sm mt-3 max-w-[380px]">
            Your voice, on broadcast grade. Pick a door below — we&apos;ll take it from there.
          </p>
        </motion.div>

        {/* Vertical stack of pills */}
        <div className="flex flex-col gap-3">
          {pills.map((p, i) => {
            const Icon = p.icon;
            const commonClass =
              "group relative w-full flex items-center gap-4 rounded-2xl px-5 py-4 bg-white/[0.035] border border-white/10 backdrop-blur-md text-left hover:bg-white/[0.06] hover:-translate-y-0.5 hover:border-white/25 transition-all overflow-hidden";
            const inner = (
              <>
                {/* Faint colored glow behind icon on hover */}
                <div
                  className="absolute -inset-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none rounded-2xl"
                  style={{
                    background: `radial-gradient(ellipse 40% 100% at 5% 50%, ${p.color}22, transparent 70%)`,
                  }}
                />
                <div
                  className="relative w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border"
                  style={{
                    backgroundColor: `${p.color}18`,
                    borderColor: `${p.color}45`,
                    color: p.color,
                  }}
                >
                  <Icon size={18} />
                </div>
                <div className="relative flex-1 min-w-0">
                  <div className="font-display font-bold text-[15px] md:text-base tracking-tight truncate">
                    {p.label}
                  </div>
                  <div className="text-[11px] text-[#a395c3] mt-0.5 truncate">
                    {p.sub}
                  </div>
                </div>
                <ArrowUpRight
                  size={16}
                  className="relative text-white/40 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0"
                />
              </>
            );

            return (
              <motion.div
                key={p.key}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: 0.12 + i * 0.05,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                {p.href ? (
                  <a
                    href={p.href}
                    target={p.external ? "_blank" : undefined}
                    rel={p.external ? "noopener noreferrer" : undefined}
                    data-testid={`links-cta-${p.key}`}
                    className={commonClass}
                  >
                    {inner}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={p.onClick}
                    data-testid={`links-cta-${p.key}`}
                    className={commonClass}
                  >
                    {inner}
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Footer credit */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="text-center mt-10 text-[10px] uppercase tracking-[0.32em] text-white/30"
        >
          © 2026 SB Media Studio · All rights reserved
        </motion.div>
      </div>

      {/* Mounted modals (event-driven from other pages, direct-driven here) */}
      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} />
      <PodcastGuestModal open={guestOpen} onClose={() => setGuestOpen(false)} />
      <DiscoveryCallModal
        open={discoveryOpen}
        onClose={() => setDiscoveryOpen(false)}
      />
      <LocationModal open={locationOpen} onClose={() => setLocationOpen(false)} />
      <ContactFormModal open={contactOpen} onClose={() => setContactOpen(false)} />
      <PricingFAQModal open={faqOpen} onClose={() => setFaqOpen(false)} />
      <PackageQuiz open={quizOpen} onClose={() => setQuizOpen(false)} />
    </div>
  );
};

export default LinksPage;
